/**
 * 开放平台「网站应用」登录测试（qrconnect + snsapi_login）。
 * 手机微信里常见「带头像、点允许」的授权页来自公众号网页授权（snsapi_userinfo），不是本页这条链路。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { recordIOSEntryUrl, useWeChatShare, type WeChatShareData } from '@hs/shared';

function isWeChatInApp(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /MicroMessenger/i.test(navigator.userAgent);
}

type BootstrapDisabled = {
  configured: false;
  message: string;
};

type BootstrapOk = {
  configured: true;
  appId: string;
  redirectUri: string;
  state: string;
  qrconnectUrl: string;
};

type ApiEnvelope<T> = {
  success?: boolean;
  data: T;
  code?: number;
  message?: string;
};

type OaAuthUrlData = {
  auth_url: string;
};

type OaUserInfoData = {
  openid?: string;
  unionid?: string;
  user_info?: {
    nickname?: string;
    headimgurl?: string;
  };
};

declare global {
  interface Window {
    WxLogin?: new (opts: Record<string, unknown>) => void;
  }
}

export default function WeChatOpenTestPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [bootstrap, setBootstrap] = useState<BootstrapDisabled | BootstrapOk | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [oaAuthLoading, setOaAuthLoading] = useState(false);
  const [oaAuthErr, setOaAuthErr] = useState<string | null>(null);
  const [oaCodeProcessing, setOaCodeProcessing] = useState(false);
  const [oaResult, setOaResult] = useState<OaUserInfoData | null>(null);
  const inWeChat = useMemo(() => isWeChatInApp(), []);

  const {
    ready: wxShareReady,
    loading: wxShareLoading,
    error: wxShareErr,
    init: initWxShare,
    setShareInfo,
  } = useWeChatShare({ jssdkSource: 'open_platform' });

  /** 公众号 OAuth 回调与当前页一致，需在公众号「网页授权域名」下 */
  const oaRedirectUri = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${window.location.pathname}`;
  }, []);

  const ok = searchParams.get('ok');
  const wechatUserId = searchParams.get('wechatUserId');
  const err = searchParams.get('error');
  const wxOauthCode = searchParams.get('code');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/wechat/open/bootstrap');
        const json = (await res.json()) as ApiEnvelope<BootstrapDisabled | BootstrapOk>;
        if (!cancelled) {
          setBootstrap(json.data);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** iOS 微信内 SPA 签名需用首次进入 URL，尽早记录 */
  useEffect(() => {
    recordIOSEntryUrl();
  }, []);

  /** 微信 JSSDK：用于 updateAppMessageShareData / updateTimelineShareData */
  useEffect(() => {
    if (!inWeChat) return;
    void initWxShare();
  }, [inWeChat, initWxShare]);

  const buildSharePayload = useCallback((): WeChatShareData => {
    const href = typeof window !== 'undefined' ? window.location.href.split('#')[0] : '';
    const nick = oaResult?.user_info?.nickname?.trim();
    const avatar = oaResult?.user_info?.headimgurl?.trim();
    return {
      title: nick ? `「${nick}」· 微信能力测试` : '微信 JSSDK 分享测试',
      desc: nick
        ? `来自 ${nick} 的分享（开放平台 / 公众号网页授权测试页）`
        : '开放平台网站登录、公众号网页授权与 JSSDK 分享测试',
      link: href,
      imgUrl: avatar || '',
    };
  }, [oaResult]);

  /** 与业务页一致：JSSDK 就绪后自动配置分享内容，不再依赖手动按钮 */
  useEffect(() => {
    if (!wxShareReady || !inWeChat) return;
    setShareInfo(buildSharePayload());
  }, [wxShareReady, inWeChat, setShareInfo, buildSharePayload]);

  /** 公众号网页授权回跳：URL 带 code 时换票并写入 wechat_users（后端 syncFromOAuth） */
  useEffect(() => {
    const code = wxOauthCode?.trim();
    if (!code) return;

    const doneKey = `oa_oauth_done_${code}`;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(doneKey)) {
      navigate({ pathname: location.pathname }, { replace: true });
      return;
    }

    let cancelled = false;
    setOaCodeProcessing(true);
    setOaAuthErr(null);
    (async () => {
      try {
        const res = await fetch(`/api/wechat/auth/userinfo?code=${encodeURIComponent(code)}`);
        const json = (await res.json()) as ApiEnvelope<OaUserInfoData>;
        if (cancelled) return;
        if (!res.ok) {
          throw new Error(json.message || `HTTP ${res.status}`);
        }
        if (!json.data) {
          throw new Error(json.message || '换取用户信息失败');
        }
        setOaResult(json.data);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(doneKey, '1');
        }
        navigate({ pathname: location.pathname }, { replace: true });
      } catch (e) {
        if (!cancelled) {
          setOaAuthErr(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setOaCodeProcessing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wxOauthCode, navigate, location.pathname]);

  const startOfficialAccountWebAuth = () => {
    setOaAuthErr(null);
    if (!oaRedirectUri) {
      setOaAuthErr('无法构造 redirect_uri');
      return;
    }
    if (!inWeChat) {
      setOaAuthErr('请在微信内打开本页后再试');
      return;
    }
    setOaAuthLoading(true);
    (async () => {
      try {
        const u = new URL('/api/wechat/auth/url', window.location.origin);
        u.searchParams.set('redirect_uri', oaRedirectUri);
        u.searchParams.set('scope', 'snsapi_userinfo');
        const res = await fetch(u.toString());
        const json = (await res.json()) as ApiEnvelope<OaAuthUrlData>;
        if (!res.ok) {
          throw new Error(json.message || `获取授权地址失败 HTTP ${res.status}`);
        }
        const authUrl = json.data?.auth_url;
        if (!authUrl) {
          throw new Error(json.message || '未返回 auth_url');
        }
        window.location.href = authUrl;
      } catch (e) {
        setOaAuthErr(e instanceof Error ? e.message : String(e));
        setOaAuthLoading(false);
      }
    })();
  };

  useEffect(() => {
    if (!bootstrap || !bootstrap.configured) return;

    const el = document.getElementById('wx_login_container');
    if (!el) return;

    const script = document.createElement('script');
    script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
    script.async = true;
    script.onload = () => {
      if (!window.WxLogin) return;
      el.innerHTML = '';
      new window.WxLogin({
        self_redirect: false,
        id: 'wx_login_container',
        appid: bootstrap.appId,
        scope: 'snsapi_login',
        redirect_uri: encodeURIComponent(bootstrap.redirectUri),
        state: bootstrap.state,
        style: 'black',
        href: '',
        fast_login: true,
      });
    };
    script.onerror = () => setLoadError('无法加载 wxLogin.js');
    document.body.appendChild(script);
    return () => {
      script.remove();
    };
  }, [bootstrap]);

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6 overflow-x-hidden px-4 py-6 pb-16 min-h-[100dvh]">
      <h1 className="text-xl font-semibold text-foreground">开放平台网站登录测试</h1>

      {loadError && (
        <p className="text-sm text-danger" role="alert">
          {loadError}
        </p>
      )}

      {(ok || err || wechatUserId) && (
        <div
          className="w-full rounded-lg border border-default-200 bg-default-50 p-4 text-sm"
          role="status"
        >
          {ok === '1' && (
            <p className="text-success">
              授权成功
              {wechatUserId ? `，wechat_users.id（Hashid）: ${wechatUserId}` : ''}
            </p>
          )}
          {err && <p className="text-danger">错误: {err}</p>}
        </div>
      )}

      {!bootstrap && !loadError && <p className="text-sm text-default-500">加载配置…</p>}

      {bootstrap && !bootstrap.configured && (
        <p className="text-warning text-sm text-center">{bootstrap.message}</p>
      )}

      {bootstrap && bootstrap.configured && (
        <>
          {/* 授权区置顶：避免 min-h-full/微信 WebView 下首屏裁切，用户看不到虚线框 */}
          <section className="w-full space-y-2" aria-label="微信登录嵌入区">
            <h2 className="text-sm font-medium text-default-700">授权区域（WxLogin）</h2>
            <p className="text-xs text-default-500">
              此为开放平台网站应用「扫码登录」组件（PC 场景为主），手机端多为二维码，需用另一台设备扫码或相机。
            </p>
            <div
              id="wx_login_container"
              className={`box-border flex min-h-[240px] w-full items-center justify-center rounded-lg border-2 border-dashed border-success-400/50 bg-content1 ${inWeChat ? 'min-h-[300px]' : ''}`}
            />
          </section>

          <section className="w-full space-y-2 rounded-lg border border-default-200 bg-default-50 p-3">
            <h2 className="text-sm font-medium text-default-800">说明</h2>
            <p className="text-xs text-default-600 leading-relaxed">
              上方为开放平台网站应用（qrconnect）；手机内「带头像」的授权请用页面底部<strong>公众号网页授权</strong>按钮。
            </p>
            {!inWeChat && (
              <p className="text-xs text-default-500">非微信内：开放平台扫码以 PC 为主；公众号授权请复制链接到微信打开。</p>
            )}
          </section>

          {!inWeChat && (
            <section className="w-full space-y-2">
              <h2 className="text-sm font-medium text-default-700">整页打开 qrconnect（调试用）</h2>
              <p className="text-xs text-default-500">
                顶层跳转会走移动版「扫一扫」流程；与微信内使用下方 WxLogin 不同。仅建议在非微信环境排查。
              </p>
              <a
                href={bootstrap.qrconnectUrl}
                className="inline-flex items-center justify-center rounded-md bg-default-200 px-4 py-2.5 text-sm font-medium text-default-800 hover:opacity-90"
              >
                整页打开 qrconnect
              </a>
            </section>
          )}

          <details className="w-full text-xs text-default-500">
            <summary className="cursor-pointer">调试：完整 qrconnect 链接</summary>
            <p className="mt-2 break-all">
              <a className="text-primary underline" href={bootstrap.qrconnectUrl}>
                {bootstrap.qrconnectUrl}
              </a>
            </p>
          </details>
        </>
      )}

      <section className="w-full space-y-3 rounded-lg border border-success-200 bg-success-50/70 p-4">
        <h2 className="text-sm font-semibold text-foreground">公众号网页授权</h2>
        <p className="text-xs text-default-600 leading-relaxed">
          跳转微信授权页（<code className="rounded bg-content1 px-1">snsapi_userinfo</code>
          ），授权后回到本页并由后端同步 <code className="rounded bg-content1 px-1">wechat_users</code>。
        </p>
        <p className="text-[11px] text-default-500 break-all">
          redirect_uri（须已在公众号「网页授权域名」内）：
          <span className="text-default-700">{oaRedirectUri || '（加载中）'}</span>
        </p>
        {oaCodeProcessing && <p className="text-xs text-default-500">正在处理授权回调…</p>}
        {oaAuthErr && (
          <p className="text-xs text-danger" role="alert">
            {oaAuthErr}
          </p>
        )}
        {oaResult && (
          <div className="rounded-md border border-success-200 bg-content1 px-3 py-2 text-xs text-success-700">
            <p>已同步：openid {oaResult.openid ?? '—'}</p>
            {oaResult.user_info?.nickname != null && <p>昵称：{oaResult.user_info.nickname}</p>}
            {oaResult.unionid && <p className="break-all">unionid：{oaResult.unionid}</p>}
          </div>
        )}
        <button
          type="button"
          disabled={oaAuthLoading || !inWeChat || !oaRedirectUri}
          onClick={startOfficialAccountWebAuth}
          className="w-full rounded-md bg-success px-4 py-3 text-sm font-medium text-success-foreground hover:opacity-90 active:opacity-80 disabled:cursor-not-allowed disabled:opacity-45"
        >
          {oaAuthLoading ? '正在跳转…' : '请求微信授权（昵称 / 头像）'}
        </button>
        {!inWeChat && (
          <p className="text-xs text-warning">请在微信内打开本页后再点击；系统浏览器无法完成公众号网页授权。</p>
        )}
      </section>

      <section className="w-full space-y-3 rounded-lg border border-primary-200 bg-primary-50/70 p-4">
        <h2 className="text-sm font-semibold text-foreground">
          开放平台网站应用 · JSSDK 分享
        </h2>
        <p className="text-xs text-default-600 leading-relaxed">
          使用<strong className="font-medium">微信开放平台网站应用</strong>的 JS-SDK 能力（后端{' '}
          <code className="rounded bg-content1 px-1">/api/wechat/open/js-sdk/config</code>
          ，<code className="rounded bg-content1 px-1">wx.config</code> 的{' '}
          <code className="rounded bg-content1 px-1">appId</code> 为网站应用 AppID）。
          分享配置会在页面初始化后自动写入（标题/描述/链接/图片），你直接点右上角「···」即可选择「发送给朋友 / 分享到朋友圈」。
        </p>
        {!inWeChat && (
          <p className="text-xs text-warning">请在微信内打开本页；非微信环境无法初始化 JSSDK。</p>
        )}
        {inWeChat && wxShareLoading && <p className="text-xs text-default-500">正在初始化 JSSDK…</p>}
        {inWeChat && wxShareErr && (
          <p className="text-xs text-danger" role="alert">
            {wxShareErr}
          </p>
        )}
        {inWeChat && wxShareReady && !wxShareLoading && (
          <p className="text-xs text-primary-700" role="status">
            分享配置已自动就绪：请点击右上角「···」发给朋友或分享到朋友圈。
          </p>
        )}
        <p className="text-[11px] text-default-500 leading-relaxed">
          分享图优先用公众号授权后的头像 URL（未授权则仅标题/描述/链接）。请在开放平台（或微信开发者平台）为该<strong>网站应用</strong>配置「JS
          接口安全域名」，与当前页域名一致。
        </p>
      </section>
    </div>
  );
}
